(() => {
  class ShippingProgressBar {
    constructor(element) {
      this.element = element;
      this.messageEl = element.querySelector('.spb__message');
      this.amountEl = element.querySelector('.spb__amount');
      this.fillEl = element.querySelector('.spb__fill');
      this.barEl = element.querySelector('.spb__bar');

      this.threshold = this.parseNumber(element.dataset.threshold);
      this.onlyWithItems = element.dataset.onlyWithItems === 'true';
      this.keepShowing = element.dataset.keepShowing === 'true';
      this.showAmountsRight = element.dataset.showAmounts === 'true';
      this.textInitial = element.dataset.textInitial || '';
      this.textBelow = element.dataset.textBelow || '';
      this.textReached = element.dataset.textReached || '';

      this.thresholdCents = Math.round(this.threshold * 100);
      this.cartCurrency = null;

      if (this.thresholdCents <= 0) {
        this.hide(true);
        return;
      }

      this.updateStickyTop = this.updateStickyTop.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);

      this.bindEvents();
      this.updateStickyTop();
      this.refresh();
    }

    parseNumber(value) {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    bindEvents() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.cartData) {
            this.update(event.cartData);
          } else {
            this.refresh();
          }
        });
      }

      document.addEventListener('cart:updated', (event) => {
        if (event.detail && event.detail.cart) {
          this.update(event.detail.cart);
        } else {
          this.refresh();
        }
      });

      document.addEventListener('cart:refresh', () => this.refresh());
      document.addEventListener('submit', this.handleSubmit, true);

      window.addEventListener('resize', this.updateStickyTop);

      if ('ResizeObserver' in window) {
        const header = this.getHeaderElement();
        if (header) {
          this.headerObserver = new ResizeObserver(() => this.updateStickyTop());
          this.headerObserver.observe(header);
        }
      }
    }

    handleSubmit(event) {
      const form = event.target;
      if (!form || form.nodeName !== 'FORM') return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;
      window.setTimeout(() => this.refresh(), 300);
    }

    getHeaderElement() {
      return (
        document.querySelector('.shopify-section-header .header-wrapper') ||
        document.querySelector('sticky-header') ||
        document.querySelector('.header-wrapper')
      );
    }

    updateStickyTop() {
      const header = this.getHeaderElement();
      if (!header) return;
      const height = header.getBoundingClientRect().height;
      this.element.style.setProperty('--spb-sticky-top', `${Math.round(height)}px`);
    }

    refresh() {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('Cart fetch failed');
          return response.json();
        })
        .then((cart) => this.update(cart))
        .catch(() => this.hide(true));
    }

    update(cart) {
      if (!cart || typeof cart.total_price !== 'number') {
        this.hide(true);
        return;
      }

      if (this.thresholdCents <= 0) {
        this.hide(true);
        return;
      }

      const itemCount = cart.item_count || 0;
      if (this.onlyWithItems && itemCount === 0) {
        this.hide(false);
        return;
      }

      this.cartCurrency = cart.currency || this.cartCurrency;
      const total = cart.total_price || 0;
      const remaining = Math.max(this.thresholdCents - total, 0);
      const reached = remaining === 0;

      if (reached && !this.keepShowing) {
        this.hide(false);
        return;
      }

      if (reached) {
        this.setMessage(this.textReached, '');
        this.setProgress(100);
        this.show();
        return;
      }

      if (total === 0) {
        const amount = this.formatMoney(this.thresholdCents);
        this.applyMessage(this.textInitial, amount);
        this.setProgress(0);
        this.show();
        return;
      }

      const formattedRemaining = this.formatMoney(remaining);
      this.applyMessage(this.textBelow, formattedRemaining);
      this.setProgress((total / this.thresholdCents) * 100);
      this.show();
    }

    applyMessage(text, amount) {
      const hasPlaceholder = this.hasPlaceholder(text);
      if (this.showAmountsRight && !hasPlaceholder) {
        const cleaned = this.stripAmountPlaceholder(text);
        this.setMessage(cleaned || text, amount);
        return;
      }

      this.setMessage(this.replaceAmountPlaceholder(text, amount), '');
    }

    replaceAmountPlaceholder(text, amount) {
      if (!text) return '';
      const replaced = text.replace(this.placeholderPattern(), amount);
      if (replaced !== text) return replaced;
      return `${text} ${amount}`.trim();
    }

    stripAmountPlaceholder(text) {
      if (!text) return '';
      return text.replace(this.placeholderPattern(), '').replace(/\s{2,}/g, ' ').trim();
    }

    hasPlaceholder(text) {
      if (!text) return false;
      return this.placeholderPattern().test(text);
    }

    placeholderPattern() {
      return /\{\{\s*amount\s*\}\}|\[\[\s*amount\s*\]\]|\{\s*amount\s*\}/g;
    }

    setMessage(message, amount) {
      if (this.messageEl) this.messageEl.textContent = message;
      if (this.amountEl) this.amountEl.textContent = amount;
    }

    setProgress(percent) {
      if (!this.fillEl) return;
      const clamped = Math.max(0, Math.min(100, percent));
      this.fillEl.style.width = `${clamped}%`;
      if (this.barEl) {
        this.barEl.setAttribute('aria-valuenow', String(Math.round(clamped)));
      }
    }

    show() {
      this.element.classList.add('is-visible');
      this.element.setAttribute('aria-hidden', 'false');
    }

    hide(reset) {
      this.element.classList.remove('is-visible');
      this.element.setAttribute('aria-hidden', 'true');
      if (reset && this.fillEl) this.fillEl.style.width = '0%';
      if (this.amountEl) this.amountEl.textContent = '';
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const format = window.money_format || window.Shopify.money_format;
        try {
          const formatted = window.Shopify.formatMoney(cents, format);
          if (this.cartCurrency === 'NOK' && /NOK/i.test(formatted)) {
            return `${formatted.replace(/NOK/gi, '').trim()} kr`.trim();
          }
          return formatted;
        } catch (error) {
          const formatted = window.Shopify.formatMoney(cents);
          if (this.cartCurrency === 'NOK' && /NOK/i.test(formatted)) {
            return `${formatted.replace(/NOK/gi, '').trim()} kr`.trim();
          }
          return formatted;
        }
      }

      const currency = this.cartCurrency || 'USD';
      if (currency === 'NOK') {
        const amount = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
        return `${amount} kr`;
      }
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('.shipping-progress-bar').forEach((element) => {
      if (element.dataset.spbInit) return;
      element.dataset.spbInit = 'true';
      new ShippingProgressBar(element);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;
    init(event.target);
  });
})();
